import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Poster from "./Poster";
import EventDetails from "./EventDetails";
import Tabs from "./Tabs";
import BackButton from "../../../components/BackButton";
import "../../../styles/EventPage.scss";
import { tryFetch, IResponse } from "../../../helpers/FetchAPI";

interface Ticket {
  id: string;
  event_id: string;
  name: string;
  description: string;
  capacity: number;
  ticket_price: string;
  created_at: string;
}

interface HouseRule {
  id: string;
  rule: string;
}

interface Event {
  id: string;
  event_type: string;
  event_title: string;
  event_slug: string;
  event_image: string;
  venue: string;
  all_dates: string[] | null;
  description: string;
  organizer: string;
  venue_type: string;
  venue_image: string;
  tickets: Ticket[];
  house_rules: HouseRule[];
  status: string;
  time_limit_minutes: number | null;
  is_seat_section: string;
  created_at: string;
  event_rating: string;
  event_url?: string;
}

const EventPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const [eventData, setEventData] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const fetchEventBySlug = async () => {
      setIsLoading(true);
      setErrorMessage("");
      setEventData(null);

      try {
        if (!slug) {
          setErrorMessage("Invalid event URL.");
          return;
        }

        /*
         * Step 1:
         * Get the event list so we can find the event ID
         * using the slug from the URL.
         */
        const eventsResponse: IResponse = await tryFetch({
          url: "/events",
          method: "GET",
        });

        if (
          !eventsResponse ||
          !eventsResponse.is_success ||
          !Array.isArray(eventsResponse.data)
        ) {
          console.error(
            "Failed to fetch events:",
            eventsResponse?.error || "Unknown error"
          );

          setErrorMessage("Unable to load the event.");
          return;
        }

        const decodedSlug = decodeURIComponent(slug).toLowerCase();

        const matchedEvent = (eventsResponse.data as Event[]).find(
          (event) =>
            event.event_slug?.toLowerCase() === decodedSlug
        );

        if (!matchedEvent) {
          setErrorMessage("Event not found.");
          return;
        }

        /*
         * Step 2:
         * Get the complete event details using the matched ID.
         */
        const eventResponse: IResponse = await tryFetch({
          url: `/events/${matchedEvent.id}`,
          method: "GET",
        });

        if (
          !eventResponse ||
          !eventResponse.is_success ||
          !eventResponse.data
        ) {
          console.error(
            "Failed to fetch event details:",
            eventResponse?.error || "Unknown error"
          );

          setErrorMessage("Unable to load the event details.");
          return;
        }

        const event = eventResponse.data as Event;

        setEventData({
          ...event,
          event_image: event.event_image || "/assets/event-def.png",
          tickets: event.tickets || [],
          house_rules: event.house_rules || [],
          all_dates: event.all_dates || [],
        });
      } catch (error) {
        console.error("Error fetching event:", error);
        setErrorMessage("Something went wrong while loading the event.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchEventBySlug();
  }, [slug]);

  if (isLoading) {
    return (
      <div className="event-page">
        <div>Loading event...</div>
      </div>
    );
  }

  if (errorMessage || !eventData) {
    return (
      <div className="event-page">
        <BackButton />

        <div className="event-error">
          <h2>Event unavailable</h2>
          <p>{errorMessage || "Event not found."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="event-page">
      <BackButton />

      <div className="event-main-section">
        <div className="event-left">
          <Poster
            image={eventData.event_image}
            title={eventData.event_title}
          />
        </div>

        <div className="event-right">
          <EventDetails
            event_id={eventData.id}
            title={eventData.event_title}
            date={eventData.all_dates}
            venue={eventData.venue}
            event_rating={eventData.event_rating}
            price={eventData.tickets[0]?.ticket_price || ""}
            event_url={eventData.event_url}
            event_slug={eventData.event_slug}
          />
        </div>
      </div>

      <Tabs
        description={eventData.description}
        beneficiary=""
        organizer={eventData.organizer}
        mapUrl={eventData.venue_image}
        tickets={eventData.tickets}
        rules={eventData.house_rules}
        all_dates={eventData.all_dates}
      />
    </div>
  );
};

export default EventPage;